import { useSelector } from 'react-redux';
import { useKFlags } from './use-k-flags';
import { selectSettings } from '@/store/bootstrap/selectors';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { selectUsers } from '@/store/users/selectors';
import { http } from '@/lib/http';
import { useAuth } from './use-auth';

// Cache duration in milliseconds (1 minute)
const CACHE_DURATION = 60 * 1000;

const getUsers = async () => {
  const { data } = await http.get('users', {
    params: {
      scope: 'email_delegatable',
    },
  });
  // Callers spread this list; guard against a non-array payload (error shape /
  // wrapped response) so the spread never throws "is not iterable".
  return Array.isArray(data) ? data : [];
};

export const useGetAllUsers = () => {
  const { feSharedEmails } = useKFlags();
  const { user } = useSelector(selectSettings) ?? {};
  const { currUser } = useAuth();

  // all users in the kula app
  const allUsers = useSelector(selectUsers);

  // Fetch users with email delegation scope using TanStack Query
  const { data: allowedFromUsers = [], isLoading: isFromUsersLoading } =
    useQuery({
      queryKey: ['users_email_delegatable'],
      queryFn: getUsers,
      staleTime: CACHE_DURATION,
    });

  // shared emails data from settings api
  const sharedEmails = useMemo(() => {
    return feSharedEmails
      ? user?.shared_emails?.map((email) => {
          return {
            ...email,
            // flag to identify if the email is a shared email
            isSharedEmail: true,
          };
        })
      : [];
  }, [feSharedEmails, user?.shared_emails]);

  // users who have access to shared emails
  const allowedFromUsersWithSharedEmails = useMemo(() => {
    return [...(sharedEmails ?? []), ...(allowedFromUsers ?? [])];
  }, [sharedEmails, allowedFromUsers]);

  const fromUsers = feSharedEmails
    ? allowedFromUsersWithSharedEmails
    : allowedFromUsers;

  const processFromUserPayload = ({
    selectedUser,
    valueKey = 'id',
    payloadKey = 'from',
    sharedEmailPayloadKey = 'ats_shared_email_id',
  }) => {
    const payload = {};

    // Check if the user has a shared email
    if (selectedUser?.isSharedEmail) {
      // For shared emails, include the shared email ID and use the current user's ID as sender
      payload[sharedEmailPayloadKey] = selectedUser?.[valueKey];
      // for share email, send the current user's id as sender
      payload[payloadKey] = currUser?.id;
    } else {
      // For non-shared emails, directly use the selectedUser's ID as the sender
      payload[payloadKey] = selectedUser?.[valueKey];
    }

    return payload;
  };

  return {
    allUsers,
    fromUsers,
    sharedEmails,
    feSharedEmails,
    isFromUsersLoading,
    processFromUserPayload,
  };
};
